import { useState, useEffect } from 'react';
import { X, Check, Pill, Droplet } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bottoni rapidi per compresse
const QUICK_DOSES = [
  { value: 0.5, label: '½' },
  { value: 1, label: '1' },
  { value: 1.5, label: '1½' },
  { value: 2, label: '2' }
];

/**
 * Popup per confermare la quantità di farmaci somministrati
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - medicines: Array<{ id, nome, dosaggio, tipo_unita, dose_standard, scorta_attuale }>
 * - famigliaId: string
 * - onConfirm: (medicinesWithDoses) => void - chiamato dopo conferma
 */
const MedicineQuantityPopup = ({ isOpen, onClose, medicines, famigliaId, onConfirm }) => {
  // State: { medicineId: doseUsata }
  const [doses, setDoses] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Reset doses quando si apre con nuovi farmaci
  useEffect(() => {
    if (isOpen && medicines?.length > 0) {
      const initialDoses = {};
      medicines.forEach(med => {
        initialDoses[med.id] = med.dose_standard || 1;
      });
      setDoses(initialDoses);
    }
  }, [isOpen, medicines]);
  
  const handleDoseChange = (medicineId, value) => {
    setDoses(prev => ({ ...prev, [medicineId]: value }));
  };
  
  const handleConfirm = async () => {
    setSaving(true);
    
    try {
      // Decrementa scorte per ogni farmaco
      const decrementPromises = medicines
        .filter(med => med.scorta_attuale !== null && med.scorta_attuale !== undefined)
        .map(med => 
          fetch(`${API_URL}/api/medicine/stock/decrement`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              medicine_id: med.id,
              quantita_usata: doses[med.id] || 1,
              famiglia_id: famigliaId
            })
          })
        );
      
      await Promise.all(decrementPromises);
      
      // Chiama callback con farmaci e dosi
      const medicinesWithDoses = medicines.map(med => ({
        ...med,
        quantita_data: doses[med.id] || 1
      }));
      
      onConfirm(medicinesWithDoses);
      onClose();
      
    } catch (error) {
      console.error('Errore decremento scorte:', error);
      // Continua comunque con la conferma
      onConfirm(medicines);
      onClose();
    } finally {
      setSaving(false);
    }
  };
  
  // Controlla se ci sono farmaci con scorte configurate
  const hasConfiguredStock = medicines?.some(m => m.scorta_attuale !== null && m.scorta_attuale !== undefined);
  
  // Auto-conferma se nessun farmaco ha scorte
  useEffect(() => {
    if (isOpen && medicines?.length > 0 && !hasConfiguredStock) {
      onConfirm(medicines);
      onClose();
    }
  }, [isOpen, medicines, hasConfiguredStock]);
  
  if (!isOpen || !hasConfiguredStock) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-5 pb-8 shadow-xl animate-slide-up max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#2C3E50]">Conferma Quantità</h3>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        
        <p className="text-sm text-[#64748B] mb-4">
          Quanto hai somministrato?
        </p>
        
        {/* Lista farmaci con dose selector */}
        <div className="space-y-4 mb-6">
          {medicines.map(med => {
            const hasStock = med.scorta_attuale !== null && med.scorta_attuale !== undefined;
            const isLiquid = med.tipo_unita === 'ml' || med.tipo_unita === 'gocce';
            const currentDose = doses[med.id] || 1;
            
            return (
              <div 
                key={med.id}
                className="bg-[#F4F7F5] rounded-2xl p-4"
              >
                {/* Nome farmaco */}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    isLiquid ? 'bg-blue-100' : 'bg-[#D5F5E3]'
                  }`}>
                    {isLiquid ? (
                      <Droplet className="w-5 h-5 text-blue-500" />
                    ) : (
                      <Pill className="w-5 h-5 text-[#27AE60]" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#2C3E50]">{med.nome}</p>
                    {med.dosaggio && (
                      <p className="text-xs text-[#64748B]">{med.dosaggio}</p>
                    )}
                  </div>
                  {hasStock && (
                    <span className="text-xs text-[#9CA3AF]">
                      Scorta: {med.scorta_attuale} {med.tipo_unita || 'unità'}
                    </span>
                  )}
                </div>
                
                {/* Dose selector */}
                {hasStock && (
                  isLiquid ? (
                    /* Input numerico per liquidi */
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        value={currentDose}
                        onChange={(e) => handleDoseChange(med.id, parseFloat(e.target.value) || 0)}
                        step="0.5"
                        min="0"
                        className="flex-1 px-4 py-2.5 rounded-xl border border-[#E5E7EB] text-center text-lg font-semibold focus:border-[#27AE60] outline-none"
                      />
                      <span className="text-[#64748B] font-medium">{med.tipo_unita || 'ml'}</span>
                    </div>
                  ) : (
                    /* Bottoni rapidi per compresse */
                    <div className="grid grid-cols-4 gap-2">
                      {QUICK_DOSES.map(dose => (
                        <button
                          key={dose.value}
                          onClick={() => handleDoseChange(med.id, dose.value)}
                          className={`py-3 rounded-xl text-lg font-bold transition-all ${
                            currentDose === dose.value
                              ? 'bg-[#27AE60] text-white shadow-lg scale-105'
                              : 'bg-white text-[#2C3E50] border border-[#E5E7EB] hover:border-[#27AE60]'
                          }`}
                        >
                          {dose.label}
                        </button>
                      ))}
                    </div>
                  )
                )}
                
                {!hasStock && (
                  <p className="text-xs text-[#9CA3AF] italic">
                    Scorte non configurate
                  </p>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Bottone conferma */}
        <Button
          onClick={handleConfirm}
          disabled={saving}
          className="w-full bg-[#27AE60] hover:bg-[#219A52] text-white py-4 rounded-xl font-semibold text-lg"
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Salvataggio...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Check className="w-5 h-5" />
              Conferma
            </span>
          )}
        </Button>
      </div>
    </div>
  );
};

export default MedicineQuantityPopup;
